import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { api } from '../utils/api';
import { motion } from 'framer-motion';
import { Crown, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { QRCodeSVG } from 'qrcode.react';

export default function UpgradePage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const { user, refreshUser } = useAuth();

  useEffect(() => {
    loadPlans();
  }, []);

  useEffect(() => {
    let interval;
    if (paymentData) {
      interval = setInterval(checkPaymentStatus, 5000);
    }
    return () => clearInterval(interval);
  }, [paymentData]);

  const loadPlans = async () => {
    try {
      const response = await api.getPlans();
      setPlans(response.data);
    } catch (error) {
      toast.error('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setProcessingPayment(true);
    try {
      const response = await api.createPixPayment(19.90);
      setPaymentData(response.data);
      toast.success('Pagamento PIX gerado!');
    } catch (error) {
      toast.error('Erro ao gerar pagamento');
    } finally {
      setProcessingPayment(false);
    }
  };

  const checkPaymentStatus = async () => {
    if (!paymentData) return;
    
    try {
      const response = await api.checkPayment(paymentData.id);
      if (response.data.status === 'RECEIVED' || response.data.status === 'CONFIRMED') {
        toast.success('Pagamento confirmado! Bem-vindo ao Premium!');
        await refreshUser();
        setPaymentData(null);
      }
    } catch (error) {
      console.error('Erro ao verificar pagamento:', error);
    }
  };

  const copyPixCode = () => {
    if (paymentData?.qr_code) {
      navigator.clipboard.writeText(paymentData.qr_code);
      toast.success('Código PIX copiado!');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (user?.plan === 'PREMIUM') {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="max-w-md w-full text-center p-8 rounded-3xl bg-white border border-border"
          >
            <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-primary to-secondary mx-auto mb-6 flex items-center justify-center">
              <Crown className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Você já é Premium!</h1>
            <p className="text-muted-foreground mb-6">
              Aproveite todo o conteúdo exclusivo disponível para você.
            </p>
          </motion.div>
        </div>
      </Layout>
    );
  }

  if (paymentData) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="max-w-md w-full p-8 rounded-3xl bg-white border border-border"
            data-testid="payment-qr-code"
          >
            <h2 className="text-2xl font-bold text-center mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Pague com PIX
            </h2>
            
            <div className="bg-muted rounded-2xl p-6 mb-6">
              {paymentData.qr_code && (
                <div className="flex justify-center mb-4">
                  <QRCodeSVG value={paymentData.qr_code} size={200} />
                </div>
              )}
              <p className="text-center text-sm text-muted-foreground mb-4">
                Escaneie o QR Code com o app do seu banco
              </p>
              <Button
                onClick={copyPixCode}
                data-testid="copy-pix-code"
                variant="outline"
                className="w-full"
              >
                Copiar Código PIX
              </Button>
            </div>

            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">Valor: R$ {paymentData.valor.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">
                Aguardando confirmação do pagamento...
              </p>
              <div className="mt-4">
                <div className="animate-spin h-6 w-6 border-4 border-primary border-t-transparent rounded-full mx-auto" />
              </div>
            </div>

            <Button
              onClick={() => setPaymentData(null)}
              variant="ghost"
              className="w-full mt-6"
            >
              Cancelar
            </Button>
          </motion.div>
        </div>
      </Layout>
    );
  }

  const premiumPlan = plans.find(p => p.tipo === 'PREMIUM');
  const freePlan = plans.find(p => p.tipo === 'FREE');

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="p-4 md:p-8 max-w-4xl mx-auto"
      >
        <div className="text-center mb-12">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="inline-flex h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-secondary items-center justify-center mb-4"
          >
            <Crown className="h-8 w-8 text-white" />
          </motion.div>
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Desbloqueie o Premium
          </h1>
          <p className="text-xl text-muted-foreground">
            Acesso ilimitado a todo conteúdo educacional
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {freePlan && <PlanCard plan={freePlan} isPremium={false} onUpgrade={handleUpgrade} processing={processingPayment} />}
          {premiumPlan && <PlanCard plan={premiumPlan} isPremium={true} onUpgrade={handleUpgrade} processing={processingPayment} />}
        </div>
      </motion.div>
    </Layout>
  );
}

function PlanCard({ plan, isPremium, onUpgrade, processing }) {
  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      data-testid={`plan-card-${plan.tipo.toLowerCase()}`}
      className={`p-8 rounded-3xl border-2 ${
        isPremium
          ? 'border-primary bg-gradient-to-br from-primary/5 to-secondary/5'
          : 'border-border bg-white'
      }`}
    >
      {isPremium && (
        <div className="inline-flex px-3 py-1 rounded-full bg-gradient-to-r from-primary to-secondary text-white text-xs font-semibold mb-4">
          RECOMENDADO
        </div>
      )}
      <h3 className="text-2xl font-bold mb-2">{plan.nome}</h3>
      <div className="flex items-baseline gap-2 mb-4">
        <span className="text-4xl font-bold">R$ {plan.preco.toFixed(2)}</span>
        {plan.preco > 0 && <span className="text-muted-foreground">/mês</span>}
      </div>
      <p className="text-muted-foreground mb-6">{plan.descricao}</p>
      <ul className="space-y-3 mb-6">
        {plan.beneficios && plan.beneficios[0] && (
          <li className="flex items-start gap-3">
            <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-sm">{plan.beneficios[0]}</span>
          </li>
        )}
        {plan.beneficios && plan.beneficios[1] && (
          <li className="flex items-start gap-3">
            <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-sm">{plan.beneficios[1]}</span>
          </li>
        )}
        {plan.beneficios && plan.beneficios[2] && (
          <li className="flex items-start gap-3">
            <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-sm">{plan.beneficios[2]}</span>
          </li>
        )}
        {plan.beneficios && plan.beneficios[3] && (
          <li className="flex items-start gap-3">
            <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-sm">{plan.beneficios[3]}</span>
          </li>
        )}
        {plan.beneficios && plan.beneficios[4] && (
          <li className="flex items-start gap-3">
            <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-sm">{plan.beneficios[4]}</span>
          </li>
        )}
      </ul>
      {isPremium && (
        <Button
          onClick={onUpgrade}
          data-testid="upgrade-button"
          disabled={processing}
          className="w-full h-12 rounded-full font-semibold text-base bg-gradient-to-r from-primary to-secondary"
        >
          {processing ? 'Processando...' : 'Assinar Agora'}
        </Button>
      )}
      {!isPremium && (
        <Button disabled className="w-full h-12 rounded-full" variant="outline">
          Plano Atual
        </Button>
      )}
    </motion.div>
  );
}
